import { authApi } from './authApi';
import { CERTIFICATE_API_BASE, getApiErrorMessage, parseApiResponse } from '../config/apiConfig';

// Certificate types
export interface CreateCertificateRequest {
  type: 'ISO' | 'Compliance';
  companyName: string;
  registeredAddress: string;
  branchAddress: string;
  standard: string;
  scope: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate: string;
  dateOfCertification?: string | null;
  firstSurveillanceAuditDue?: string | null;
  secondSurveillanceAuditDue?: string | null;
  registrationDate?: string | null;
  surveillanceDate?: string | null;
}

export interface CertificateResponse {
  id: string;
  type: 'ISO' | 'Compliance';
  companyName: string;
  registeredAddress: string;
  branchAddress: string;
  standard: string;
  scope: string;
  certificateNumber: string;
  issueDate: string;
  expiryDate: string;
  dateOfCertification?: string | null;
  firstSurveillanceAuditDue?: string | null;
  secondSurveillanceAuditDue?: string | null;
  registrationDate?: string | null;
  surveillanceDate?: string | null;
  status: 'Valid' | 'Expired' | 'Suspended';
  qrCodeUrl: string;
  createdDate: string;
}

export interface VerificationResponse {
  isFound: boolean;
  message: string;
  certificate?: CertificateResponse;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// API Service class
class CertificateApi {
  private baseUrl: string;

  constructor(baseUrl: string = CERTIFICATE_API_BASE) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    const token = authApi.getToken();

    return token
      ? {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      : {
          'Content-Type': 'application/json',
        };
  }

  private getDownloadHeaders(): HeadersInit {
    const token = authApi.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async createCertificate(
    request: CreateCertificateRequest
  ): Promise<{ success: boolean; data?: CertificateResponse; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request),
      });

      const data = await parseApiResponse<{ success?: boolean; data?: CertificateResponse; message?: string }>(response);
      const responseData = data as { success?: boolean; data?: CertificateResponse; message?: string } | null;

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to create certificate'),
        };
      }

      return {
        success: Boolean(responseData?.success ?? true),
        data: responseData?.data,
        error: responseData?.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async deleteCertificate(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await parseApiResponse<{ success?: boolean; message?: string }>(response);

      if (!response.ok) {
        return { success: false, error: getApiErrorMessage(data, 'Failed to delete certificate') };
      }

      return { success: Boolean((data as any)?.success ?? true) };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Network error' };
    }
  }

  async getCertificates(): Promise<{
    success: boolean;
    data?: CertificateResponse[];
    error?: string;
  }> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await parseApiResponse<{ success?: boolean; data?: CertificateResponse[]; message?: string }>(response);
      const responseData = data as { success?: boolean; data?: CertificateResponse[]; message?: string } | null;

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to load certificates'),
        };
      }

      return {
        success: Boolean(responseData?.success ?? true),
        data: responseData?.data || [],
        error: responseData?.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async verifyCertificate(
    certificateNumber: string
  ): Promise<{
    success: boolean;
    data?: VerificationResponse;
    error?: string;
  }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/verify/${encodeURIComponent(certificateNumber)}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await parseApiResponse<VerificationResponse>(response);

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to verify certificate'),
        };
      }

      return {
        success: true,
        data: data as VerificationResponse | undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async getCertificateById(
    id: string
  ): Promise<{
    success: boolean;
    data?: CertificateResponse;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await parseApiResponse<{ success?: boolean; data?: CertificateResponse; message?: string }>(response);
      const responseData = data as { success?: boolean; data?: CertificateResponse; message?: string } | null;

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to get certificate'),
        };
      }

      return {
        success: Boolean(responseData?.success ?? true),
        data: responseData?.data,
        error: responseData?.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async downloadCertificatePdf(
    id: string
  ): Promise<{ success: boolean; error?: string }> {
    return this.downloadCertificate(id, 'pdf');
  }

  async downloadCertificate(
    id: string,
    format: string = 'pdf'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/${id}/download?format=${encodeURIComponent(format)}`,
        {
          method: 'GET',
          headers: this.getDownloadHeaders(),
        }
      );

      if (!response.ok) {
        const data = await parseApiResponse<{ message?: string }>(response);
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to download certificate'),
        };
      }

      const blob = await response.blob();
      if (!blob.size) {
        return {
          success: false,
          error: 'The generated file was empty',
        };
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const ext = (format || 'pdf').toLowerCase() === 'png' || (format || '').toLowerCase() === 'image' ? 'png' : 'pdf';
      link.download = this.getDownloadFileName(response, id, ext);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  private getDownloadFileName(response: Response, fallbackId: string, fallbackExt = 'pdf'): string {
    const contentDisposition = response.headers.get('content-disposition');
    const encodedMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
    const fileName = encodedMatch?.[1] || plainMatch?.[1];
    if (fileName) {
      try {
        return decodeURIComponent(fileName);
      } catch {
        return fileName;
      }
    }

    return `certificate-${fallbackId}.${fallbackExt}`;
  }
}

export const certificateApi = new CertificateApi();
